import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Avatar,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Button,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Tooltip,
    Chip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    Work,
    LocationOn,
    Event,
    Description,
    Badge,
    Edit,
    Upload,
    Refresh,
    CheckCircle,
    Warning,
    Cancel,
    InsertDriveFile,
    LinkedIn,
    Twitter,
    YouTube,
    Business,
    Image as ImageIcon,
    CorporateFare,
    Flag,
    Home,
    Language,
    EventNote,
    Assignment,
    Hotel,
    MailOutline,
    ContactMail,
    CreditCard,
    Download as DownloadIcon // Add this import
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import swal from 'sweetalert';
import { useDeadline } from '../../contexts/DeadlineContext';

const StatusIndicator = styled(Box)(({ theme, status }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    fontWeight: 600,
    fontSize: '0.75rem',
    backgroundColor:
        status === 1 ? theme.palette.success.light :
            status === 0 ? theme.palette.warning.light :
                theme.palette.error.light,
    color:
        status === 1 ? theme.palette.success.contrastText :
            status === 0 ? theme.palette.warning.contrastText :
                theme.palette.error.contrastText,
}));

const DocumentCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        transform: 'translateY(-2px)'
    }
}));

const SpeakerDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const localStorageloginParticpantID = localStorage.getItem('loginParticpantID');
    const { abstractHomeSetup } = useDeadline();
    const gradientBackground = '#05597D';// Example gradient background
    const [speaker, setSpeaker] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [downloading, setDownloading] = useState(false);
    const statusLabels = {
        0: { label: 'Pending Approval', icon: <Warning fontSize="small" /> },
        1: { label: 'Approved', icon: <CheckCircle fontSize="small" /> },
        2: { label: 'Rejected', icon: <Cancel fontSize="small" /> }
    };

    const genderLabels = {
        1: 'Male',
        2: 'Female',
        3: 'Other'
    };

    const booleanLabels = {
        1: 'Yes',
        0: 'No'
    };

    const fetchSpeaker = async () => {
        try {
            const response = await axios.get(`/api/speaker/profile/${localStorageloginParticpantID}`);
            setSpeaker(response.data.userID);
        } catch (err) {
            console.error(err);
            setError('Failed to load speaker profile');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSpeaker();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return dateString;
        }
    };

    const calculateProfileCompletion = (speaker) => {
        if (!speaker) return 0;
        const requiredFields = [
            speaker.name_title,
            speaker.participant_name,
            speaker.participant_email,
            speaker.participant_profession,
            speaker.participant_phone_number,
            speaker.image,
            speaker.participant_gender,
            speaker.participant_country,
            speaker.participant_city,
            speaker.participant_company,
            speaker.speaker_details?.biography,
            speaker.speaker_details?.nationality,
            speaker.speaker_details?.passport_number,
            speaker.speaker_details?.passport_expiry,
            speaker.speaker_details?.photo_path,
            speaker.speaker_details?.company_logo_path
        ];
        const filledCount = requiredFields.filter(field =>
            field !== null && field !== '' && field !== undefined
        ).length;
        return Math.round((filledCount / requiredFields.length) * 100);
    };


    const fetchFiles = async () => {
        try {
            const res = await axios.get('/api/org-get-speaker-announcement', {
                params: { eventid: localStorage.getItem('eventid') }
            });

            if (res.data.status === 200) {
                setAnnouncement(res.data.message); // store the object
                console.log('Announcement fetched successfully:', res.data.message);
            }
        } catch (err) {
            console.error('Failed to fetch announcement', err);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);



    if (loading) {
        return (
            <Box
                pt={isMobile ? 5 : 10}
                pl={isMobile ? 0 : 70}
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
            >
                <CircularProgress size={isMobile ? 40 : 60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={isMobile ? 2 : 4}>
                <Alert severity="error" action={
                    <Button color="inherit" size="small" onClick={fetchSpeaker}>
                        Retry
                    </Button>
                }>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!speaker) {
        return (
            <Box p={isMobile ? 2 : 4}>
                <Alert severity="warning">No speaker data available</Alert>
            </Box>
        );
    }

    const profileCompletion = calculateProfileCompletion(speaker);

    const handleDownload = (url, filename) => {
        // Extract the base filename if needed
        const cleanFilename = filename.split('/').pop().split('?')[0].split('#')[0];

        // Create a temporary link
        const link = document.createElement('a');
        link.href = url;
        link.download = cleanFilename; // force download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const eventid = localStorage.getItem('eventid');
    const staticChange = eventid == 68;

    return (
        <Box sx={{
            maxWidth: 1200,
            mx: 'auto',
            pt: isMobile ? 8 : 13,
            px: isMobile ? 1 : 3
        }}>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'flex-start' : 'center'}
                mb={3}
                spacing={isMobile ? 2 : 0}
            >
                {
                    staticChange ?
                        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
                            Hospital Profile
                        </Typography> :
                        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
                            Speaker Profile
                        </Typography>
                }
                {/* <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
                    Speaker Profile
                </Typography> */}
                <Stack
                    direction="row"
                    spacing={1}
                    width={isMobile ? '100%' : 'auto'}
                    justifyContent={isMobile ? 'space-between' : 'flex-end'}
                >
                    <Tooltip title="Refresh data">
                        <IconButton onClick={fetchSpeaker} size={isMobile ? 'small' : 'medium'}>
                            <Refresh fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                    </Tooltip>


                    <Button
                        color='success'
                        size={isMobile ? 'small' : 'medium'}
                        variant='contained'
                        component={Link}
                        to="/admin/conference/upload-org-file"
                        sx={{
                            background: abstractHomeSetup.color_code_two || gradientBackground,
                            ":hover": {
                                color: "white"
                            }
                        }}
                    >
                        {isMobile ? 'Download' : 'Download Documents'}
                    </Button>

                    <Button
                        color='success'
                        size={isMobile ? 'small' : 'medium'}
                        variant='contained'
                        component={Link}
                        to="/admin/conference/speaker-upload-documents"
                        sx={{
                            background: abstractHomeSetup.color_code_two || gradientBackground,
                            ":hover": {
                                color: "white"
                            }
                        }}
                    >
                        {isMobile ? 'Upload' : 'Upload Documents'}
                    </Button>
                    {
                        !staticChange && (
                            <Button
                                color='success'
                                size={isMobile ? 'small' : 'medium'}
                                variant='contained'
                                component={Link}
                                to={`/admin/conference/speaker-edit/${speaker?.id}`}
                                sx={{
                                    background: abstractHomeSetup.color_code_two || gradientBackground,
                                    ":hover": {
                                        color: "white"
                                    }
                                }}
                            >
                                {isMobile ? 'Edit' : 'Edit Profile'}
                            </Button>
                        )
                    }

                </Stack>
            </Stack>



            {/* Announcement Marquee with Hover Pause */}
            {/* {
                announcement?.conference_announcement?.trim() && (
                    <Box
                        sx={{
                            mb: 3,
                            backgroundColor: abstractHomeSetup.color_code_two || gradientBackground,
                            borderRadius: 1,
                            p: 1,
                            borderLeft: `4px solid ${theme.palette.warning.dark}`,
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover .marquee-text': {
                                animationPlayState: 'paused', // Pause on hover
                            },
                        }}
                    >
                        <Box
                            className="marquee-text"
                            sx={{
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                paddingLeft: '100%',
                                animation: 'marquee 15s linear infinite',
                                color: theme.palette.warning.contrastText,
                                fontWeight: 500,
                                fontSize: isMobile ? '0.875rem' : '1rem',
                                py: 1,
                                '@keyframes marquee': {
                                    '0%': { transform: 'translateX(0)' },
                                    '100%': { transform: 'translateX(-100%)' },
                                },
                            }}
                        >
                            {announcement?.conference_announcement || 'No announcements available.'}
                        </Box>
                    </Box>
                )
            } */}
            {
                announcement?.conference_announcement?.trim() && (
                    <Box
                        sx={{
                            mb: 3,
                            backgroundColor: abstractHomeSetup.color_code_two || gradientBackground,
                            borderRadius: 1,
                            p: isMobile ? 0.5 : 1,
                            borderLeft: `4px solid ${theme.palette.warning.dark}`,
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover .marquee-text': {
                                animationPlayState: 'paused', // Pause on hover
                            },
                        }}
                    >
                        <Box
                            className="marquee-text"
                            sx={{
                                display: 'inline-block',
                                whiteSpace: isMobile ? 'normal' : 'nowrap',
                                paddingLeft: isMobile ? 0 : '100%',
                                animation: isMobile ? 'marquee-mobile 10s linear infinite' : 'marquee 15s linear infinite',
                                color: theme.palette.warning.contrastText,
                                fontWeight: 500,
                                fontSize: isMobile ? '0.8rem' : '1rem',
                                lineHeight: 1.4,
                                py: 1,
                                '@keyframes marquee': {
                                    '0%': { transform: 'translateX(0)' },
                                    '100%': { transform: 'translateX(-100%)' },
                                },
                                '@keyframes marquee-mobile': {
                                    '0%': { transform: 'translateX(0)' },
                                    '100%': { transform: 'translateX(-60%)' },
                                },
                            }}
                        >
                            {announcement?.conference_announcement || 'No announcements available.'}
                        </Box>
                    </Box>
                )
            }



            {/* Profile Completion */}
            <Box mb={3}>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    justifyContent="space-between"
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    spacing={isMobile ? 1 : 0}
                >
                    <Typography variant="subtitle1" gutterBottom>
                        Profile Completion: {profileCompletion}%
                    </Typography>
                    <Chip
                        label={profileCompletion === 100 ? "Complete" : "Incomplete"}
                        color={profileCompletion === 100 ? "success" : "warning"}
                        variant="outlined"
                        size={isMobile ? 'small' : 'medium'}
                    />
                </Stack>
                <Box sx={{ width: '100%', backgroundColor: '#eee', borderRadius: 10 }}>
                    <Box
                        sx={{
                            width: `${profileCompletion}%`,
                            backgroundColor:
                                profileCompletion === 100 ? 'green' :
                                    profileCompletion >= 50 ? 'orange' : 'red',
                            height: 10,
                            borderRadius: 10,
                            transition: 'width 0.3s ease-in-out'
                        }}
                    />
                </Box>
            </Box>


            <Grid container spacing={isMobile ? 2 : 3}>
                {/* Left Column - Basic Info */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardHeader
                            avatar={
                                <Avatar
                                    src={speaker.image}
                                    alt={speaker.participant_name}
                                    sx={{
                                        width: isMobile ? 60 : 80,
                                        height: isMobile ? 60 : 80
                                    }}
                                />
                            }
                            title={
                                <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                                    {speaker.name_title} {speaker.participant_name}
                                </Typography>
                            }
                            subheader={
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    alignItems={isMobile ? 'flex-start' : 'center'}
                                    spacing={1}
                                    mt={1}
                                >
                                    {statusLabels[speaker.is_active]?.icon}
                                    <StatusIndicator status={speaker.is_active}>
                                        {statusLabels[speaker.is_active]?.label}
                                    </StatusIndicator>
                                    <Chip
                                        label={staticChange ? "Hospital" : "Speaker"}
                                        size="small"
                                        color="primary"
                                    />
                                </Stack>
                            }
                            sx={{
                                '& .MuiCardHeader-content': {
                                    overflow: 'hidden'
                                }
                            }}
                        />
                        {speaker.speaker_details?.rejection_reason && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'error.main',
                                    px: 2,
                                    fontWeight: 500,
                                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                                }}
                            >
                                Rejection Reason: {speaker.speaker_details.rejection_reason}
                            </Typography>
                        )}

                        <CardContent>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                        <Email fontSize={isMobile ? 'small' : 'medium'} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Email"
                                        secondary={
                                            <Link
                                                href={`mailto:${speaker.participant_email}`}
                                                style={{
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {speaker.participant_email}
                                            </Link>
                                        }
                                        primaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            fontWeight: 'medium'
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                        <Phone fontSize={isMobile ? 'small' : 'medium'} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Phone"
                                        secondary={
                                            <Link
                                                href={`tel:${speaker.participant_phone_number}`}
                                                style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                            >
                                                {speaker.participant_phone_number}
                                            </Link>
                                        }
                                        primaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            fontWeight: 'medium'
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                        <Work fontSize={isMobile ? 'small' : 'medium'} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Profession"
                                        secondary={speaker.participant_profession || 'N/A'}
                                        secondaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            sx: { opacity: 0.8 }
                                        }}
                                        primaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            fontWeight: 'medium'
                                        }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                        <Person fontSize={isMobile ? 'small' : 'medium'} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Gender"
                                        secondary={genderLabels[speaker.participant_gender] || 'N/A'}
                                        secondaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            sx: { opacity: 0.8 }
                                        }}
                                        primaryTypographyProps={{
                                            variant: isMobile ? 'body2' : 'body1',
                                            fontWeight: 'medium'
                                        }}
                                    />
                                </ListItem>
                                {
                                    !staticChange && (
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <Business fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Company line"
                                                secondary={speaker.participant_company || 'N/A'}
                                                secondaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    sx: { opacity: 0.8 }
                                                }}
                                                primaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        </ListItem>
                                    )
                                }

                            </List>
                        </CardContent>
                    </Card>

                    {/* Social Links */}
                    {!staticChange && speaker.speaker_details && (
                        <Card sx={{ mt: 2 }}>
                            <CardHeader
                                title="Social Links"
                                titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                            />
                            <CardContent>
                                <List dense>
                                    {speaker.speaker_details.linkedin && (
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <LinkedIn color="primary" fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <Link
                                                href={speaker.speaker_details.linkedin}
                                                target="_blank"
                                                style={{
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                LinkedIn Profile
                                            </Link>
                                        </ListItem>
                                    )}
                                    {speaker.speaker_details.twitter && (
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <Twitter color="info" fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <Link
                                                href={speaker.speaker_details.twitter}
                                                target="_blank"
                                                style={{
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                Twitter Profile
                                            </Link>
                                        </ListItem>
                                    )}
                                    {speaker.speaker_details.youtube && (
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <YouTube color="error" fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <Link
                                                href={speaker.speaker_details.youtube}
                                                target="_blank"
                                                style={{
                                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                YouTube Channel
                                            </Link>
                                        </ListItem>
                                    )}
                                </List>
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                {/* Right Column - Detailed Info */}
                <Grid item xs={12} md={8}>
                    <Card>

                        <CardHeader
                            title={staticChange ? "Hospital Information" : "Speaker Details"}
                            titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                        />
                        <CardContent>
                            <Grid container spacing={isMobile ? 1 : 3}>
                                {/* Location Information */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <LocationOn sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                        Location Information
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <Flag fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Country"
                                                secondary={staticChange ? "Saudi Arabia" : (speaker.participant_country || 'N/A')}
                                                primaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    fontWeight: 'medium'
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    sx: { opacity: 0.8 }
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <Home fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="City"
                                                secondary={speaker.participant_city || 'N/A'}
                                                primaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    fontWeight: 'medium'
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    sx: { opacity: 0.8 }
                                                }}
                                            />
                                        </ListItem>
                                        {
                                            !staticChange && (
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <CorporateFare fontSize={isMobile ? 'small' : 'medium'} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Organization "
                                                        secondary={speaker.participant_hostpital_organization || 'N/A'}
                                                        primaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            fontWeight: 'medium'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            sx: { opacity: 0.8 }
                                                        }}
                                                    />
                                                </ListItem>)
                                        }

                                    </List>
                                </Grid>

                                {/* Event Information */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <EventNote sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                        Event Information
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                <Event fontSize={isMobile ? 'small' : 'medium'} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Event ID"
                                                secondary={speaker.eventName || 'N/A'}
                                                primaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    fontWeight: 'medium'
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: isMobile ? 'body2' : 'body1',
                                                    sx: { opacity: 0.8 }
                                                }}
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>

                                {speaker.speaker_details && (
                                    <>
                                        <Grid item xs={12}>
                                            <Divider />
                                        </Grid>

                                        {/* Biography */}
                                        <Grid item xs={12} md={6} >
                                            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Description sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                                {staticChange ? "Applicant Name who represent the institution and his/her Emai" : "Speaker Biography"}
                                            </Typography>
                                            <Paper variant="outlined" sx={{ p: 2, minHeight: 100 }}>
                                                <Typography variant={isMobile ? 'body2' : 'body1'}>
                                                    {speaker.speaker_details.biography || 'No biography provided'}
                                                </Typography>
                                            </Paper>
                                        </Grid>

                                        {/* Travel Information */}
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Language sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                                {staticChange ? "Hospital Details" : "Travel Information"}
                                            </Typography>
                                            <List dense>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <Flag fontSize={isMobile ? 'small' : 'medium'} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={staticChange ? "Sector: Private or Government" : "Nationality"}
                                                        secondary={speaker.speaker_details.nationality || 'N/A'}
                                                        primaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            fontWeight: 'medium'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            sx: { opacity: 0.8 }
                                                        }}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <CreditCard fontSize={isMobile ? 'small' : 'medium'} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={staticChange ? "Bed Capacity" : "Passport Number"}
                                                        secondary={speaker.speaker_details.passport_number || 'N/A'}
                                                        primaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            fontWeight: 'medium'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            sx: { opacity: 0.8 }
                                                        }}
                                                    />
                                                </ListItem>

                                                <ListItem sx={{ display: staticChange ? 'none' : 'flex' }}>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <EventNote fontSize={isMobile ? 'small' : 'medium'} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Passport Expiry"
                                                        secondary={formatDate(speaker.speaker_details.passport_expiry)}
                                                        primaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            fontWeight: 'medium'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            sx: { opacity: 0.8 }
                                                        }}
                                                    />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <Hotel fontSize={isMobile ? 'small' : 'medium'} sx={{ display: staticChange ? 'none' : 'flex' }} />
                                                    </ListItemIcon>
                                                    {
                                                        staticChange ? (
                                                            <ListItemText
                                                                primary="Is your antimicrobial stewardship program activity involved in national programs and initiatives (i.e. submitting KPI's, participate in awareness campaigns, reporting to PHA)?"
                                                                secondary={booleanLabels[speaker.speaker_details.needs_accommodation] || 'N/A'}
                                                                primaryTypographyProps={{
                                                                    variant: isMobile ? 'body2' : 'body1',
                                                                    fontWeight: 'medium'
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    variant: isMobile ? 'body2' : 'body1',
                                                                    sx: { opacity: 0.8 }
                                                                }}
                                                            />
                                                        ) : (
                                                            <ListItemText
                                                                primary="Needs Accommodation"
                                                                secondary={booleanLabels[speaker.speaker_details.needs_accommodation] || 'N/A'}
                                                                primaryTypographyProps={{
                                                                    variant: isMobile ? 'body2' : 'body1',
                                                                    fontWeight: 'medium'
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    variant: isMobile ? 'body2' : 'body1',
                                                                    sx: { opacity: 0.8 }
                                                                }}
                                                            />
                                                        )
                                                    }
                                                </ListItem>



                                                <ListItem sx={{ display: staticChange ? 'none' : 'flex' }}>
                                                    <ListItemIcon sx={{ minWidth: isMobile ? 36 : 56 }}>
                                                        <MailOutline fontSize={isMobile ? 'small' : 'medium'} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Needs Invitation Letter"
                                                        secondary={booleanLabels[speaker.speaker_details.needs_invitation_letter] || 'N/A'}
                                                        primaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            fontWeight: 'medium'
                                                        }}
                                                        secondaryTypographyProps={{
                                                            variant: isMobile ? 'body2' : 'body1',
                                                            sx: { opacity: 0.8 }
                                                        }}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Documents Section */}
                    <Card sx={{ mt: 3 }}>
                        <CardHeader
                            title="Documents"
                            titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                        />
                        <CardContent>
                            <Grid container spacing={isMobile ? 1 : 2}>
                                {
                                    !staticChange && (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                                <ImageIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                                Photos
                                            </Typography>
                                            {speaker.speaker_details?.company_logo_path && (
                                                <DocumentCard>
                                                    <InsertDriveFile sx={{ mr: 2 }} />
                                                    <Box>
                                                        <Typography variant={isMobile ? 'body2' : 'body1'}>Company Logo</Typography>
                                                        <img
                                                            src={speaker?.speaker_details?.company_logo_path}
                                                            alt="Company Logo"
                                                            style={{
                                                                width: isMobile ? 80 : 120,
                                                                height: isMobile ? 60 : 100,
                                                                borderRadius: 8,
                                                                marginTop: 8
                                                            }}
                                                        />
                                                    </Box>
                                                </DocumentCard>
                                            )}
                                        </Grid>
                                    )
                                }

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <ContactMail sx={{ verticalAlign: 'middle', mr: 1, fontSize: isMobile ? '1rem' : '1.25rem' }} />
                                        Other Documents
                                    </Typography>
                                    {speaker.documents && speaker.documents.length > 0 && (
                                        <Box>
                                            {Object.entries(
                                                speaker.documents.reduce((acc, doc) => {
                                                    const title = doc.title || 'Untitled';
                                                    if (!acc[title]) acc[title] = [];
                                                    acc[title].push(doc);
                                                    return acc;
                                                }, {})
                                            ).map(([title, docs]) => (
                                                <Box key={title} sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant={isMobile ? 'subtitle2' : 'h6'}
                                                        gutterBottom
                                                        sx={{ fontWeight: 'bold' }}
                                                    >
                                                        {title}
                                                    </Typography>

                                                    {docs.map((doc) => {
                                                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(doc.uploaded_file);

                                                        const handleDelete = async () => {
                                                            if (!window.confirm('Are you sure you want to delete this document?')) return;
                                                            try {
                                                                await axios.post(`/api/speaker/documents-delete/${doc.id}`);
                                                                swal('Success', 'Document deleted successfully', 'success');
                                                                fetchSpeaker(); // refresh after delete
                                                            } catch (error) {
                                                                console.error('Failed to delete document', error);
                                                                alert('Failed to delete document.');
                                                            }
                                                        };

                                                        return (
                                                            <DocumentCard
                                                                sx={{
                                                                    mb: 1,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between', // <-- keeps left content & right actions apart
                                                                    p: isMobile ? 1 : 2,
                                                                }}
                                                            >
                                                                {/* LEFT SIDE (icon / name) */}
                                                                {isImage ? (
                                                                    <img
                                                                        src={doc.uploaded_file_url}
                                                                        alt={doc.title}
                                                                        style={{
                                                                            width: isMobile ? 40 : 60,
                                                                            height: isMobile ? 40 : 60,
                                                                            objectFit: 'cover',
                                                                            marginRight: isMobile ? 8 : 16,
                                                                            borderRadius: 4,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <InsertDriveFile sx={{ fontSize: isMobile ? 30 : 40 }} />
                                                                )}

                                                                {/* RIGHT SIDE (Download + Delete together) */}
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Tooltip title="Download / View">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                const fileUrl = doc.uploaded_file_url;
                                                                                const extension = doc.uploaded_file.split('.').pop().toLowerCase();

                                                                                // List of image extensions
                                                                                const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

                                                                                if (imageExtensions.includes(extension)) {
                                                                                    // Open image in a new tab
                                                                                    window.open(fileUrl, '_blank');
                                                                                } else {
                                                                                    // Otherwise download
                                                                                    handleDownload(fileUrl, `${doc.title}.${extension}`);
                                                                                }
                                                                            }}
                                                                            disabled={downloading}
                                                                        >
                                                                            {downloading ? <CircularProgress size={24} /> : <DownloadIcon />}
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Tooltip title="Delete Document">
                                                                        <IconButton onClick={handleDelete}>
                                                                            <DeleteIcon sx={{ color: 'red' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>

                                                            </DocumentCard>

                                                        );
                                                    })}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {speaker.participant_badge && (
                                        <DocumentCard>
                                            <InsertDriveFile sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography variant={isMobile ? 'body2' : 'body1'}>Event Badge</Typography>
                                                <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                                                    {speaker.participant_badge}
                                                </Typography>
                                            </Box>
                                        </DocumentCard>
                                    )}
                                    {speaker.participant_certificate && (
                                        <DocumentCard>
                                            <InsertDriveFile sx={{ mr: 2 }} />
                                            <Box>
                                                <Typography variant={isMobile ? 'body2' : 'body1'}>Certificate</Typography>
                                                <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
                                                    {speaker.participant_certificate}
                                                </Typography>
                                            </Box>
                                        </DocumentCard>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* System Information */}
            <Box mt={3}>
                <Card variant="outlined">
                    <CardHeader
                        title="System Information"
                        subheader="Technical details about this record"
                        titleTypographyProps={{ variant: isMobile ? 'subtitle1' : 'h6' }}
                        subheaderTypographyProps={{ variant: isMobile ? 'caption' : 'body2' }}
                    />
                    <CardContent>
                        <Grid container spacing={isMobile ? 1 : 2}>
                            <Grid item xs={12} md={6}>
                                <List dense>
                                    <ListItem>
                                        <ListItemText
                                            primary="Registration ID"
                                            secondary={speaker.id}
                                            primaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                fontWeight: 'medium'
                                            }}
                                            secondaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                sx: { opacity: 0.8 }
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Created At"
                                            secondary={formatDate(speaker.created_at)}
                                            primaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                fontWeight: 'medium'
                                            }}
                                            secondaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                sx: { opacity: 0.8 }
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <List dense>
                                    <ListItem>
                                        <ListItemText
                                            primary="Updated At"
                                            secondary={formatDate(speaker.updated_at)}
                                            primaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                fontWeight: 'medium'
                                            }}
                                            secondaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                sx: { opacity: 0.8 }
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Import Status"
                                            secondary={booleanLabels[speaker.is_exported] || 'N/A'}
                                            primaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                fontWeight: 'medium'
                                            }}
                                            secondaryTypographyProps={{
                                                variant: isMobile ? 'body2' : 'body1',
                                                sx: { opacity: 0.8 }
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default SpeakerDashboard;